import React, { useEffect, useState } from 'react';
import moment from 'moment';
import './style.css';
import '../user-list/user-list.css';
import { useDispatch, useSelector } from 'react-redux';

import { getAllQrEventLogsApi, getQrEventsApi } from '../../../api/adminApi';
import { adminSelector } from '../../../redux/slicers/adminSlice';

import DashboardLayout from '../dashboardLayout';
import { PulseLoader } from 'react-spinners';
import Pagination from '../../../components/common/Pagination';
import { useDebouncedEffect } from '../../../helpers/hooks';
import { DEBOUNCE_DELAY } from '../../../config/constants/projectConstant';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

const QrEventLogsList = () => {
	const dispatch = useDispatch();

	const { qrEventLogsList = [] } = useSelector(adminSelector);

	const [searchQuery, setSearchQuery] = useState('');
	const [eventFilter, setEventFilter] = useState('');
	const [loading, setLoading] = useState(false);
	const [listSize, setListSize] = useState(0);
	const [pageNumbers, setPageNumbers] = useState([]);
	const [limit, setLimit] = useState({ start: 1, end: 3 });
	const [paginationSize, setPaginationSize] = useState(25);
	const [page, setPage] = useState(1);
	const [events, setEvents] = useState([]);

	const [viewOpen, setViewOpen] = useState(false);
	const [selectedLog, setSelectedLog] = useState(null);

	const getQrEventLogsList = (search, eventFilter) => {
		dispatch(
			getAllQrEventLogsApi(
				paginationSize,
				setListSize,
				page,
				setLoading,
				search,
				eventFilter,
			),
		);
	};

	const loadEvents = () => {
		dispatch(getQrEventsApi(setEvents, () => {}));
	};

	useDebouncedEffect(
		() => {
			setPage(1);
			getQrEventLogsList(searchQuery, eventFilter);
		},
		DEBOUNCE_DELAY,
		[searchQuery, eventFilter],
	);

	useEffect(() => {
		getQrEventLogsList(searchQuery, eventFilter);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, paginationSize]);

	useEffect(() => {
		loadEvents();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const pageCount =
			parseInt(listSize / paginationSize) +
			(listSize % paginationSize > 0 ? 1 : 0);
		let pages = [];
		for (let i = 1; i <= pageCount; i++) {
			pages.push(i);
		}
		setPageNumbers(pages);
	}, [listSize, paginationSize]);

	const handlePaginationChange = (event) => {
		setPaginationSize(parseInt(event.target.value));
		setPage(1);
	};

	const handleSearchChange = (e) => {
		setSearchQuery(e.target.value);
	};

	const handleEventFilterChange = (e) => {
		setEventFilter(e.target.value);
	};

	const handleViewOpen = (log) => {
		setSelectedLog(log);
		setViewOpen(true);
	};

	const handleViewClose = () => {
		setViewOpen(false);
		setSelectedLog(null);
	};

	const formatBedNumber = (userDetails) => {
		if (userDetails?.bedNo) {
			return userDetails.bedNo;
		}
		return 'N/A';
	};

	return (
		<DashboardLayout>
			<div className=''>
				<div className='title'>
					<h1>QR Event Logs</h1>
					<div className='search-filters'>
						<input
							className='search'
							type='text'
							value={searchQuery}
							onChange={handleSearchChange}
							placeholder='Search by ID, name, or email...'
						/>
						<select
							className='event-filter'
							value={eventFilter}
							onChange={handleEventFilterChange}
						>
							<option value=''>All Events</option>
							{events.map((event) => (
								<option key={event._id} value={event._id}>
									{event.displayName}
								</option>
							))}
						</select>
					</div>
				</div>

				<div className='flex table-lists'>
					<div className='flex-large'>
						<table className='table' style={{ border: '1px solid #e9ecef' }}>
							<thead>
								<tr>
									<th style={{ width: '12%' }}>Scan Date</th>
									<th style={{ width: '15%' }}>Event</th>
									<th style={{ width: '18%' }}>User</th>
									<th style={{ width: '10%' }}>User ID</th>
									<th style={{ width: '12%' }}>Email</th>
									<th style={{ width: '10%' }}>Bed No</th>
									<th style={{ width: '15%' }}>Scanned By</th>
									<th style={{ width: '8%' }}></th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr className='loading-tr'>
										<td colSpan={8}>
											<div className='loading-table-wrapper'>
												<PulseLoader
													color='#0e5472'
													style={{ display: 'flex', justifyContent: 'center' }}
												/>
											</div>
										</td>
									</tr>
								) : qrEventLogsList.length > 0 ? (
									qrEventLogsList.map((log) => (
										<tr key={log._id}>
											<td>
												{moment(log.scanTime).format('DD MMM YYYY')} <br />
												{moment(log.scanTime).format('hh:mm A')}
											</td>
											<td>
												<strong>{log.eventId?.displayName}</strong>
												<br />
												<small className='text-muted'>
													{log.eventId?.name}
												</small>
											</td>
											<td>
												<strong>{log.userId?.name}</strong>
												{log.timeSlot && (
													<>
														<br />
														<small className='text-info'>
															Time: {log.timeSlot}
														</small>
													</>
												)}
											</td>
											<td>{log.userId?.id}</td>
											<td>{log.userId?.email || 'N/A'}</td>
											<td>{formatBedNumber(log.metadata?.userDetails)}</td>
											<td>
												{log.scannedBy?.firstName} {log.scannedBy?.lastName}
												{log.scanLocation && (
													<>
														<br />
														<small className='text-muted'>
															@ {log.scanLocation}
														</small>
													</>
												)}
											</td>
											<td>
												<div className='button-div'>
													<button
														className='btn btn-primary'
														onClick={() => handleViewOpen(log)}
													>
														View
													</button>
												</div>
											</td>
										</tr>
									))
								) : (
									<tr>
										<td colSpan={8}>
											<h1 style={{ textAlign: 'center' }}>
												No QR event logs found
											</h1>
										</td>
									</tr>
								)}
							</tbody>
						</table>

						<div className='pagination'>
							<div>
								<h1 className='paginationSize'>Total Logs: {listSize}</h1>
							</div>
							<div className='paginationSize'>
								<label htmlFor='pagination-size'>Select Page Size:&nbsp;</label>
								<select
									id='pagination-size'
									value={paginationSize}
									onChange={handlePaginationChange}
								>
									<option value='15'>15</option>
									<option value='25'>25</option>
									<option value='50'>50</option>
									<option value='75'>75</option>
									<option value='100'>100</option>
								</select>
							</div>
							<div className='pagination'>
								<Pagination
									setPage={setPage}
									limit={limit}
									pageNumbers={pageNumbers}
									setLimit={setLimit}
									page={page}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<Dialog
				className='dialog'
				fullWidth={true}
				maxWidth={'md'}
				open={viewOpen}
				onClose={handleViewClose}
				aria-labelledby='view-log-dialog-title'
			>
				<DialogTitle id='view-log-dialog-title'>
					QR Event Log Details
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{selectedLog ? (
							<table className='table'>
								<tbody>
									<tr>
										<td style={{ width: '30%' }}>Scan Date & Time:</td>
										<td>
											{moment(selectedLog.scanTime).format(
												'DD MMM YYYY hh:mm A',
											)}
										</td>
									</tr>
									<tr>
										<td>Event:</td>
										<td>
											<strong>{selectedLog.eventId?.displayName}</strong>
											<br />
											<small>({selectedLog.eventId?.name})</small>
										</td>
									</tr>
									<tr>
										<td>User:</td>
										<td>
											<strong>{selectedLog.userId?.name}</strong>
											<br />
											ID: {selectedLog.userId?.id}
											{selectedLog.userId?.email && (
												<>
													<br />
													Email: {selectedLog.userId?.email}
												</>
											)}
											{selectedLog.userId?.contactNumber && (
												<>
													<br />
													Contact: {selectedLog.userId?.contactNumber}
												</>
											)}
										</td>
									</tr>
									<tr>
										<td>Scanned By:</td>
										<td>
											{selectedLog.scannedBy?.firstName}{' '}
											{selectedLog.scannedBy?.lastName}
										</td>
									</tr>
									{selectedLog.timeSlot && (
										<tr>
											<td>Time Slot:</td>
											<td>{selectedLog.timeSlot}</td>
										</tr>
									)}
									{selectedLog.scanLocation && (
										<tr>
											<td>Scan Location:</td>
											<td>{selectedLog.scanLocation}</td>
										</tr>
									)}
									{selectedLog.notes && (
										<tr>
											<td>Notes:</td>
											<td>{selectedLog.notes}</td>
										</tr>
									)}
									{selectedLog.metadata?.userDetails && (
										<tr>
											<td>User Details:</td>
											<td>
												<table className='table table-bordered'>
													<tbody>
														{selectedLog.metadata.userDetails.bedNo && (
															<tr>
																<td style={{ fontWeight: 'bold' }}>Bed No</td>
																<td>
																	{selectedLog.metadata.userDetails.bedNo}
																</td>
															</tr>
														)}
														{selectedLog.metadata.userDetails.masjidName && (
															<tr>
																<td style={{ fontWeight: 'bold' }}>Masjid</td>
																<td>
																	{selectedLog.metadata.userDetails.masjidName}
																</td>
															</tr>
														)}
													</tbody>
												</table>
											</td>
										</tr>
									)}
								</tbody>
							</table>
						) : (
							<p>No log data</p>
						)}
					</DialogContentText>
				</DialogContent>
				<DialogActions className='dialog-actions'>
					<Button variant='contained' onClick={handleViewClose}>
						CLOSE
					</Button>
				</DialogActions>
			</Dialog>
		</DashboardLayout>
	);
};

export default QrEventLogsList;
